import { useState } from 'react';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Icon } from '@sdb/planning-ui';
import { showErrorSnackbar } from 'components/shared/Snackbar';

import { MessageAction, MessageActionType } from '../types';
import { LEAVE_REQUEST_TASK_KEYS } from '../constants';
import useSubmitTaskAction from '../hooks/useSubmitTaskAction';
import LeaveRequestProposalDialog from './LeaveRequestProposalDialog';
import RejectDialog from './RejectDialog';
import {
  ActionButton,
  ActionButtonsContainer
} from './MessageActionButtons.styles';

interface MessageActionButtonsProps {
  taskId: string;
  taskDefinitionKey: string;
  leaveRequestId?: string;
  leaveType?: string;
  actions: MessageAction[];
  onActionComplete?: () => void;
}

const ACTION_ICONS = {
  [MessageActionType.ACCEPT]: 'check',
  [MessageActionType.APPROVE]: 'check',
  [MessageActionType.REJECT]: 'xmark',
  [MessageActionType.SEEN]: 'envelope-open-text',
  [MessageActionType.AVAILABLE]: 'check',
  [MessageActionType.NOT_AVAILABLE]: 'xmark'
} as const;

const ACTION_COLOR_MAP: Record<string, 'success' | 'error' | 'default'> = {
  [MessageActionType.ACCEPT]: 'success',
  [MessageActionType.APPROVE]: 'success',
  [MessageActionType.REJECT]: 'error',
  [MessageActionType.SEEN]: 'default',
  [MessageActionType.AVAILABLE]: 'success',
  [MessageActionType.NOT_AVAILABLE]: 'error'
};

const REJECT_ACTIONS = new Set<string>([
  MessageActionType.REJECT,
  MessageActionType.NOT_AVAILABLE
]);

const ACCEPT_ACTIONS = new Set<string>([
  MessageActionType.ACCEPT,
  MessageActionType.APPROVE
]);

const MessageActionButtons = ({
  taskId,
  taskDefinitionKey,
  leaveRequestId,
  leaveType,
  actions,
  onActionComplete
}: MessageActionButtonsProps) => {
  const { t } = useTranslation('common');
  const { mutateAsync, isLoading } = useSubmitTaskAction();
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [pendingRejectAction, setPendingRejectAction] =
    useState<MessageActionType | null>(null);

  const isLeaveRequest = (
    LEAVE_REQUEST_TASK_KEYS as readonly string[]
  ).includes(taskDefinitionKey);

  if (!actions?.length) return null;

  const availableActions = Object.entries(actions[0] || {}).sort(
    ([, a], [, b]) => {
      if (a === MessageActionType.SEEN) return -1;
      if (b === MessageActionType.SEEN) return 1;
      return 0;
    }
  );

  const handleAction = async (actionType: MessageActionType) => {
    if (isLeaveRequest && ACCEPT_ACTIONS.has(actionType)) {
      if (!leaveRequestId) {
        showErrorSnackbar();
        return;
      }

      setLeaveDialogOpen(true);
      return;
    }
    if (REJECT_ACTIONS.has(actionType)) {
      setPendingRejectAction(actionType);
      setRejectDialogOpen(true);
      return;
    }
    try {
      await mutateAsync({
        taskId,
        actionType,
        taskDefinitionKey,
        leaveRequestId
      });
      onActionComplete?.();
    } catch {
      // Error snackbar is handled by the mutation's onError callback
    }
  };

  const handleRejectConfirm = async (note: string) => {
    if (pendingRejectAction) {
      try {
        await mutateAsync({
          taskId,
          actionType: pendingRejectAction,
          note,
          taskDefinitionKey,
          leaveRequestId
        });
        setRejectDialogOpen(false);
        setPendingRejectAction(null);
        onActionComplete?.();
      } catch {
        // Error snackbar is handled by the mutation's onError callback
      }
    }
  };

  return (
    <ActionButtonsContainer onClick={(e) => e.stopPropagation()}>
      {availableActions.map(([key, value]) => {
        const actionType = value as MessageActionType;
        const iconType =
          ACTION_ICONS[actionType as keyof typeof ACTION_ICONS] ?? 'circle';
        const color = ACTION_COLOR_MAP[actionType] || 'default';
        const plannerKey = `plannerMessages.actions.${actionType}`;
        const employeeKey = `employeeMessages.actions.${actionType}`;
        const label =
          t(plannerKey, { defaultValue: '' }) ||
          t(employeeKey, { defaultValue: actionType });

        return (
          <Tooltip key={key} title={label}>
            <ActionButton
              size="small"
              color={color}
              disabled={isLoading}
              onClick={() => handleAction(actionType)}
            >
              <Icon type={iconType} fontSize="small" />
            </ActionButton>
          </Tooltip>
        );
      })}

      {leaveRequestId ? (
        <LeaveRequestProposalDialog
          open={leaveDialogOpen}
          leaveRequestId={leaveRequestId}
          leaveType={leaveType}
          onClose={() => setLeaveDialogOpen(false)}
          onSuccess={onActionComplete}
        />
      ) : null}

      <RejectDialog
        open={rejectDialogOpen}
        variant={isLeaveRequest ? 'leave' : 'default'}
        onClose={() => {
          setRejectDialogOpen(false);
          setPendingRejectAction(null);
        }}
        onConfirm={handleRejectConfirm}
        isLoading={isLoading}
      />
    </ActionButtonsContainer>
  );
};

export default MessageActionButtons;
